import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell, useDensity, type Density, type NavGroup } from '@firmeto/design/AppShell';
import * as Icon from '@firmeto/design/Icons';
import { api, ApiError, type Operator } from './api';
import { AUTH_PATHS, meansNoSession, restoreSession, signIn, signOut, userManager } from './auth';
import { detectLocale, dictionary, localeNames, locales, rememberLocale, type Dictionary, type Locale } from './i18n';
import { parsePath, useRoute, type Route } from './routing';
import { Credits } from './pages/Credits';
import { Cycle } from './pages/Cycle';
import { Providers } from './pages/Providers';
import { Companies, CompanyCard } from './pages/Companies';
import { Offices, OfficeCard } from './pages/Offices';
import { Pricing } from './pages/Pricing';
import { TaxOffices } from './pages/TaxOffices';
import { InboundMail } from './pages/InboundMail';
import { Templates, TemplateEditor } from './pages/Templates';
import { Schemes, SchemeEditor } from './pages/Schemes';

/**
 * Panel operatora — ta sama powłoka i te same style co aplikacja klienta, inny realm i inne API.
 *
 * Sesję odzyskujemy po cichu z ciasteczka Keycloaka; bez niej ekran wejścia z jednym przyciskiem.
 * Po zalogowaniu operator ląduje w kolejce kredytów — to jest jego codzienna praca.
 */
export function App() {
  const [locale, setLocale] = useState<Locale>(detectLocale);
  const t = useMemo(() => dictionary(locale), [locale]);
  const [operator, setOperator] = useState<Operator | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [route, go] = useRoute();
  const [density, setDensity] = useDensity('firmeto-panel-gestosc');
  /** Licznik przy kolejce — tylko liczba, którą naprawdę mamy. */
  const [pending, setPending] = useState<number | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const user = await restoreSession();
      if (!user) {
        setOperator(null);
        return;
      }
      setOperator(await api.me());
    } catch (failure) {
      setOperator(null);
      if (failure instanceof ApiError && failure.code !== 'unauthorized') setError(failure.message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    // Powrót z Keycloaka: wymiana kodu na token, potem czysty adres i powrót tam, skąd się wyszło
    if (window.location.pathname === AUTH_PATHS.callback) {
      let target: Route = { screen: 'start' };
      userManager
        .signinRedirectCallback()
        .then((user) => {
          const from = (user.state as { path?: string } | undefined)?.path;
          if (from) target = parsePath(from);
        })
        .catch((failure: unknown) => {
          if (!meansNoSession(failure)) setError(failure instanceof Error ? failure.message : String(failure));
        })
        .finally(() => {
          go(target, { replace: true });
          void load();
        });
      return;
    }
    void load();
  }, [load, go]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Zalogowany operator nie stoi na stronie startowej — jego miejsce to kolejka
  useEffect(() => {
    if (!busy && operator && route.screen === 'start') go({ screen: 'credits' }, { replace: true });
  }, [busy, operator, route.screen, go]);

  useEffect(() => {
    if (!operator) return;
    api
      .credits()
      .then((items) => setPending(items.length))
      .catch(() => setPending(null));
  }, [operator]);

  function changeLocale(next: Locale) {
    setLocale(next);
    rememberLocale(next);
  }

  if (busy) {
    return (
      <main className="entry">
        <p className="lead">{t.loading}</p>
      </main>
    );
  }

  if (!operator) {
    return <Entry t={t} locale={locale} error={error} onLocale={changeLocale} onSignIn={() => void signIn()} />;
  }

  const groups: NavGroup[] = [
    {
      label: t.groups.billing,
      entries: [
        { key: 'credits', label: t.credits.open, icon: Icon.Fee, badge: pending ?? undefined },
        { key: 'cycle', label: t.cycle.open, icon: Icon.Calendar },
        { key: 'pricing', label: t.pricing.open, icon: Icon.Balance },
        { key: 'providers', label: t.providers.open, icon: Icon.Fee },
      ],
    },
    {
      label: t.groups.clients,
      entries: [
        { key: 'companies', label: t.companies.open, icon: Icon.Office },
        { key: 'offices', label: t.offices.open, icon: Icon.Users },
      ],
    },
    {
      label: t.groups.config,
      entries: [
        { key: 'templates', label: t.templates.open, icon: Icon.Book },
        { key: 'schemes', label: t.schemes.open, icon: Icon.Gear },
        { key: 'taxOffices', label: t.taxOffices.open, icon: Icon.Doc },
        { key: 'inboundMail', label: t.inboundMail.open, icon: Icon.Upload },
      ],
    },
  ];
  // Pozycja nawigacji dla ekranu — szczegóły podświetlają swoją listę
  const sectionOf: Record<Route['screen'], string> = {
    start: 'credits',
    credits: 'credits',
    pricing: 'pricing',
    cycle: 'cycle',
    providers: 'providers',
    companies: 'companies',
    company: 'companies',
    offices: 'offices',
    office: 'offices',
    templates: 'templates',
    template: 'templates',
    schemes: 'schemes',
    schemeNew: 'schemes',
    scheme: 'schemes',
    taxOffices: 'taxOffices',
    inboundMail: 'inboundMail',
  };
  const current = sectionOf[route.screen];
  const sectionLabel: Record<string, string> = {
    credits: t.credits.open,
    pricing: t.pricing.open,
    cycle: t.cycle.open,
    providers: t.providers.open,
    companies: t.companies.open,
    offices: t.offices.open,
    templates: t.templates.open,
    schemes: t.schemes.open,
    taxOffices: t.taxOffices.open,
    inboundMail: t.inboundMail.open,
  };
  const listOf: Record<string, Route> = {
    credits: { screen: 'credits' },
    pricing: { screen: 'pricing' },
    cycle: { screen: 'cycle' },
    providers: { screen: 'providers' },
    companies: { screen: 'companies' },
    offices: { screen: 'offices' },
    templates: { screen: 'templates' },
    schemes: { screen: 'schemes' },
    taxOffices: { screen: 'taxOffices' },
    inboundMail: { screen: 'inboundMail' },
  };

  return (
    <AppShell
      brand={t.appName}
      context={{ title: t.panel, subtitle: operator.email }}
      groups={groups}
      current={current}
      onSelect={(key) => go(listOf[key] ?? { screen: 'credits' })}
      crumbs={
        <>
          <b>{t.panel}</b>
          <span aria-hidden="true">›</span>
          <span>{sectionLabel[current]}</span>
        </>
      }
      controls={
        <Controls t={t} locale={locale} density={density} onDensity={setDensity} onLocale={changeLocale} />
      }
      account={{ fullName: operator.fullName, role: t.role }}
      navLabel={t.navigation}
    >
      {route.screen === 'companies' && <Companies t={t} onOpen={(id) => go({ screen: 'company', companyId: id })} />}
      {route.screen === 'company' && (
        <CompanyCard
          companyId={route.companyId}
          t={t}
          locale={locale}
          onBack={() => go({ screen: 'companies' })}
          onOffice={(id) => go({ screen: 'office', officeId: id })}
        />
      )}
      {route.screen === 'pricing' && <Pricing t={t} locale={locale} />}
      {route.screen === 'taxOffices' && <TaxOffices t={t} locale={locale} />}
      {route.screen === 'inboundMail' && <InboundMail t={t} locale={locale} />}
      {route.screen === 'offices' && <Offices t={t} onOpen={(id) => go({ screen: 'office', officeId: id })} />}
      {route.screen === 'office' && (
        <OfficeCard
          officeId={route.officeId}
          t={t}
          locale={locale}
          onBack={() => go({ screen: 'offices' })}
          onCompany={(id) => go({ screen: 'company', companyId: id })}
        />
      )}
      {route.screen === 'templates' && <Templates t={t} onOpen={(id) => go({ screen: 'template', templateId: id })} />}
      {route.screen === 'template' && <TemplateEditor templateId={route.templateId} t={t} onBack={() => go({ screen: 'templates' })} />}
      {route.screen === 'schemes' && (
        <Schemes t={t} locale={locale} onOpen={(id) => go({ screen: 'scheme', schemeId: id })} onNew={() => go({ screen: 'schemeNew' })} />
      )}
      {(route.screen === 'scheme' || route.screen === 'schemeNew') && (
        <SchemeEditor
          key={route.screen === 'scheme' ? route.schemeId : 'nowy'}
          schemeId={route.screen === 'scheme' ? route.schemeId : null}
          t={t}
          locale={locale}
          onBack={() => go({ screen: 'schemes' })}
          onSaved={(id) => go({ screen: 'scheme', schemeId: id })}
        />
      )}
      {route.screen === 'providers' && <Providers t={t} locale={locale} />}
      {route.screen === 'cycle' && (
        <Cycle
          t={t}
          locale={locale}
          onCompany={(id) => go({ screen: 'company', companyId: id })}
          onOffice={(id) => go({ screen: 'office', officeId: id })}
        />
      )}
      {(route.screen === 'credits' || route.screen === 'start') && (
        <Credits
          t={t}
          locale={locale}
          onCompany={(id) => go({ screen: 'company', companyId: id })}
          onOffice={(id) => go({ screen: 'office', officeId: id })}
          onChanged={setPending}
        />
      )}
    </AppShell>
  );
}

/** Gęstość, język i wylogowanie — pasek górny, tak jak w aplikacji, plus przycisk wyjścia. */
function Controls({
  t,
  locale,
  density,
  onDensity,
  onLocale,
}: {
  t: Dictionary;
  locale: Locale;
  density: Density;
  onDensity: (next: Density) => void;
  onLocale: (next: Locale) => void;
}) {
  return (
    <div className="sp">
      <div className="seg" role="group" aria-label={t.density}>
        <button type="button" aria-pressed={density === 'comfortable'} onClick={() => onDensity('comfortable')}>
          {t.comfortable}
        </button>
        <button type="button" aria-pressed={density === 'dense'} onClick={() => onDensity('dense')}>
          {t.dense}
        </button>
      </div>
      <select
        className="inp w sm"
        aria-label={t.language}
        value={locale}
        onChange={(event) => onLocale(event.target.value as Locale)}
      >
        {locales.map((code) => (
          <option key={code} value={code}>
            {localeNames[code]}
          </option>
        ))}
      </select>
      <button type="button" className="btn ghost sm" onClick={() => void signOut()}>
        {t.signOut}
      </button>
    </div>
  );
}

/**
 * Ekran wejścia — ta sama rama i te same klasy, co strona startowa aplikacji (entry.css),
 * tylko bez wyboru ścieżki i bez rejestracji: do panelu nie da się zapisać, tu się tylko wchodzi.
 */
function Entry({
  t,
  locale,
  error,
  onLocale,
  onSignIn,
}: {
  t: Dictionary;
  locale: Locale;
  error: string | null;
  onLocale: (next: Locale) => void;
  onSignIn: () => void;
}) {
  return (
    <main className="entry">
      <div className="frame">
        <div className="side">
          <div className="brand">
            <Icon.Mark white />
            <b>{t.appName}</b>
          </div>
          <div>
            <div className="claim">{t.entry.title}</div>
            <p className="lead">{t.entry.lead}</p>
          </div>
          <div className="side-foot">
            <span>{t.panel}</span>
          </div>
        </div>

        <div className="pane">
          <div className="top">
            <span className="kicker">{t.panel}</span>
            <div className="langsel" role="group" aria-label={t.language}>
              {locales.map((code) => (
                <button key={code} type="button" aria-pressed={code === locale} onClick={() => onLocale(code)}>
                  {localeNames[code]}
                </button>
              ))}
            </div>
          </div>

          <div className="hd">
            <h3>{t.entry.signIn}</h3>
            <p>{t.entry.paneHint}</p>
          </div>

          {error && (
            <p className="lead" role="alert">
              {error}
            </p>
          )}

          <div className="row">
            <button type="button" className="btn" onClick={onSignIn}>
              {t.entry.signIn}
              <Icon.Arrow />
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
